import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { accountService } from '@/services/account.service'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { ArrowLeft } from 'lucide-react'
import type { Account } from '@/types/account.types'

const formSchema = z.object({
  name: z.string().min(1, 'Account name is required').max(100),
  description: z.string().optional(),
  accountType: z.enum(['customer', 'dealer', 'distributor']),
  parentAccountId: z.string().optional(),
  country: z.string().optional(),
  webhookUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
})

type FormValues = z.infer<typeof formSchema>

export default function AccountNewPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      accountType: 'customer',
      parentAccountId: '',
      country: '',
      webhookUrl: '',
    },
  })

  // Fetch accounts for parent selection
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await accountService.getAccounts()
        // Filter to only show dealer and distributor accounts as parents
        const parentAccounts = response.accounts.filter(
          (acc) => acc.accountType === 'dealer' || acc.accountType === 'distributor'
        )
        setAccounts(parentAccounts)
      } catch (error) {
        console.error('Failed to fetch accounts:', error)
        toast({
          title: 'Error',
          description: 'Failed to fetch parent accounts',
          variant: 'destructive',
        })
      } finally {
        setLoadingAccounts(false)
      }
    }
    fetchAccounts()
  }, [toast])

  const onSubmit = async (data: FormValues) => {
    try {
      setLoading(true)

      // Clean up empty optional fields
      const cleanedData = {
        ...data,
        description: data.description || undefined,
        parentAccountId: data.parentAccountId || undefined,
        country: data.country || undefined,
        webhookUrl: data.webhookUrl || undefined,
      }

      await accountService.createAccount(cleanedData)

      toast({
        title: 'Success',
        description: 'Account created successfully',
      })

      navigate('/accounts')
    } catch (error: any) {
      console.error('Failed to create account:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create account',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const accountType = form.watch('accountType')

  return (
    <div className="container mx-auto py-6 max-w-3xl">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate('/accounts')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Accounts
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Create New Account</CardTitle>
          <CardDescription>
            Add a new account to the system. Customer accounts require a parent account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter account name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="dealer">Dealer</SelectItem>
                        <SelectItem value="distributor">Distributor</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The type of account determines its permissions and hierarchy
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {accountType === 'customer' && (
                <FormField
                  control={form.control}
                  name="parentAccountId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Parent Account</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={loadingAccounts}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={
                              loadingAccounts ? "Loading accounts..." : "Select parent account (optional)"
                            } />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="">None</SelectItem>
                          {accounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name} ({account.accountType})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Customer accounts can be assigned to a dealer or distributor
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter account description (optional)"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter country (optional)" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="webhookUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Webhook URL</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://example.com/webhook (optional)"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      URL to receive notifications for account events
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Account'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/accounts')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}